import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from '../../Services/firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const number = parseFloat(value);
    if (isNaN(number)) return value.toString();
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const UnitPage = () => {
    const [user, setUser] = useState(null);
    const [kibaloName, setKibaloName] = useState('');
    const [costData, setCostData] = useState({
        'Establishment': 0,
        'Depreciation': 0,
        'Maintenance': 0,
        'Interest': 0,
        'Variable': 0
    });

    const [yieldPerAcre, setYieldPerAcre] = useState('');
    const [annualYield, setAnnualYield] = useState(0);
    const [totalCost, setTotalCost] = useState(0);

    const [establishmentCosts, setEstablishmentCosts] = useState([]);
    const [equipmentCosts, setEquipmentCosts] = useState([]);
    const [otherStartupCosts, setOtherStartupCosts] = useState([]);

    const [grossRevenue, setGrossRevenue] = useState([]);

    const [showAddRevenueModal, setShowAddRevenueModal] = useState(false);
    const [newRevenue, setNewRevenue] = useState({ name: '', unit: '', quantity: '', costPrice: '' });

    const navigate = useNavigate();
    const { kibaloId } = useParams();
    const cleanKibaloId = kibaloId ? kibaloId.split('/')[0] : kibaloId;

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            if (authUser) setUser(authUser);
            else navigate('/login');
        });
        return () => unsubscribe();
    }, [navigate]);

    const fetchData = useCallback(async () => {
        if (!user || !cleanKibaloId) return;

        try {
            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            const kibaloSnap = await getDoc(kibaloRef);

            if (kibaloSnap.exists()) {
                const data = kibaloSnap.data();
                setKibaloName(data.name || data.enterpriseName);

                const costs = {
                    'Establishment Costs': data.establishmentCostPerYear || 0,
                    'Depreciation Costs': data.depreciationTable?.reduce((acc, item) => acc + parseFloat(item.depreciationAmount || '0'), 0) || 0,
                    'Other Annual Fixed Costs': data.maintenanceTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
                    'Interest Costs': data.interestTable?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0,
                    'Variable Costs': data.variableCosts?.reduce((acc, item) => acc + parseFloat(item.amount || '0'), 0) || 0
                };
                setCostData(costs);
                const totalCosts = Object.values(costs).reduce((sum, value) => sum + value, 0);
                setTotalCost(totalCosts);

                if (data.yieldPerAcre) setYieldPerAcre(data.yieldPerAcre);
                if (data.unitCostPerAcre) setAnnualYield(data.unitCostPerAcre);
                if (data.grossRevenue) setGrossRevenue(data.grossRevenue);
                if (data.establishmentTable) setEstablishmentCosts(data.establishmentTable);
                if (data.equipmentCosts) setEquipmentCosts(data.equipmentCosts);
                if (data.otherStartupCosts) setOtherStartupCosts(data.otherStartupCosts);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [user, cleanKibaloId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const calculateAnnualYield = async () => {
        const enteredYield = parseFloat(yieldPerAcre);
        if (!enteredYield || enteredYield <= 0) {
            alert("Please enter a valid Yield per Acre greater than 0.");
            return;
        }
        const newAnnualYield = parseFloat((totalCost / enteredYield).toFixed(2));
        setAnnualYield(newAnnualYield);

        if (user && cleanKibaloId) {
            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            await updateDoc(kibaloRef, {
                yieldPerAcre: enteredYield,
                unitCostPerAcre: newAnnualYield
            });
        }
    };

    const totalGrossRevenue = grossRevenue.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const grossProfit = totalGrossRevenue - totalCost;
    const totalEstablishmentCost = establishmentCosts.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);

    const startupCapital =
        totalEstablishmentCost +
        (costData['Depreciation Costs'] || 0) +
        (costData['Other Annual Fixed Costs'] || 0) +
        (costData['Interest Costs'] || 0) +
        (0.4 * (costData['Variable Costs'] || 0));


    // ---------- REVENUE HANDLERS ----------
    const handleAddRevenue = async () => {
        const quantity = parseFloat(newRevenue.quantity) || 0;
        const costPrice = parseFloat(newRevenue.costPrice) || 0;
        const amount = quantity * costPrice;

        const updatedRevenue = [...grossRevenue, { ...newRevenue, quantity, costPrice, amount }];
        setGrossRevenue(updatedRevenue);
        setNewRevenue({ name: '', unit: '', quantity: '', costPrice: '' });
        setShowAddRevenueModal(false);

        if (user && cleanKibaloId) {
            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            await updateDoc(kibaloRef, { grossRevenue: updatedRevenue });
        }
    };

    const handleDeleteRevenue = async (index) => {
        const updatedRevenue = grossRevenue.filter((_, i) => i !== index);
        setGrossRevenue(updatedRevenue);
        if (user && cleanKibaloId) {
            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            await updateDoc(kibaloRef, { grossRevenue: updatedRevenue });
        }
    };

    const handleEditRevenue = (index) => {
        const item = grossRevenue[index];
        setNewRevenue(item);
        setShowAddRevenueModal(true);
        handleDeleteRevenue(index);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-800 uppercase mb-6">
                    Gross Profit Calculation for {kibaloName} ENTERPRISE
                </h1>

                {/* ---------- COST TABLE ---------- */}
                <div className="mt-6">
                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border px-4 py-2 text-left">Cost Category</th>
                                <th className="border px-4 py-2 text-left">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(costData).map(([category, total], index) => (
                                <tr key={index}>
                                    <td className="border px-4 py-2">{category}</td>
                                    <td className="border px-4 py-2">{formatNumberWithCommas(total)}</td>
                                </tr>
                            ))}
                            <tr className="bg-gray-200 font-bold">
                                <td className="border px-4 py-2">Total Cost</td>
                                <td className="border px-4 py-2">{formatNumberWithCommas(totalCost)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* ---------- ANNUAL YIELD ---------- */}
                <div className="mt-8 p-4 bg-blue-50 rounded-lg shadow-inner border border-blue-100">
                    <h2 className="font-bold text-lg mb-2 text-blue-800">Annual Yield / Product</h2>
                    <div className="flex flex-col gap-2">
                        <input
                            type="number"
                            value={yieldPerAcre}
                            onChange={e => setYieldPerAcre(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                            placeholder="Enter Annual Yield"
                            min="0.01"
                            step="any"
                        />
                        <button
                            onClick={calculateAnnualYield}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Calculate Annual Yield
                        </button>
                    </div>
                    <div className="mt-4 p-3 bg-white border border-gray-200 rounded-md shadow-sm text-center">
                        <p className="text-gray-600 font-semibold">Cost Price / Unit</p>
                        <p className="text-xl font-bold text-blue-600 mt-1">
                            {annualYield ? formatNumberWithCommas(annualYield) : '-'}
                        </p>
                    </div>
                </div>

                {/* ---------- GROSS REVENUE ---------- */}
                <div className="mt-8 p-4 bg-green-50 rounded-lg shadow-inner border border-green-100">
                    <h2 className="font-bold text-lg mb-4 text-green-800">Gross Revenue Per Year</h2>
                    <table className="w-full border-collapse border border-gray-300 mb-4">
                        <thead>
                            <tr className="bg-green-200">
                                <th className="border px-3 py-2 text-left">Item</th>
                                <th className="border px-3 py-2 text-left">Unit</th>
                                <th className="border px-3 py-2 text-left">Quantity</th>
                                <th className="border px-3 py-2 text-left">Cost Price</th>
                                <th className="border px-3 py-2 text-left">Amount</th>
                                <th className="border px-3 py-2 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grossRevenue.length > 0 ? grossRevenue.map((item, idx) => (
                                <tr key={idx}>
                                    <td className="border px-3 py-2">{item.name}</td>
                                    <td className="border px-3 py-2">{item.unit}</td>
                                    <td className="border px-3 py-2">{item.quantity}</td>
                                    <td className="border px-3 py-2">{formatNumberWithCommas(item.costPrice)}</td>
                                    <td className="border px-3 py-2">{formatNumberWithCommas(item.amount)}</td>
                                    <td className="border px-3 py-2 flex gap-2">
                                         <button
                                            onClick={() => handleEditRevenue(idx)}
                                            className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 transition"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRevenue(idx)}
                                            className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td className="border px-3 py-2 text-center" colSpan={6}>No sales revenue added yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <p className="font-bold text-green-700 mb-2">
                        Total Gross Revenue Per Year: {formatNumberWithCommas(totalGrossRevenue)}
                    </p>
                    <button
                        onClick={() => setShowAddRevenueModal(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Add Sale Revenue
                    </button>
                </div>

                {/* ---------- GROSS PROFIT ---------- */}
                <div className="mt-8 p-4 bg-green-50 rounded-lg shadow-inner border border-green-100">
                    <h2 className="font-bold text-lg mb-4 text-green-800">Gross Profit</h2>
                    <p>Gross Revenue: {formatNumberWithCommas(totalGrossRevenue)}</p>
                    <p>Total Cost: {formatNumberWithCommas(totalCost)}</p>
                    <p>Gross Profit/Loss: {formatNumberWithCommas(grossProfit)}</p>
                </div>

                {/* ---------- PROFIT/LOSS ANALYSIS ---------- */}
                <div className={`mt-4 p-4 rounded-lg shadow-inner border ${grossProfit >= 0 ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400'}`}>
                    <h2 className="font-bold text-lg mb-2">Profit/Loss Analysis</h2>
                    <p className="font-semibold">{grossProfit >= 0 ? 'Profit' : 'Loss'}: {formatNumberWithCommas(grossProfit)}</p>
                </div>

                {/* ---------- STARTUP CAPITAL ---------- */}
                <div className="mt-8 p-4 bg-indigo-50 rounded-lg shadow-inner border border-indigo-200">
                    <h2 className="font-bold text-lg mb-2 text-indigo-800">Startup Capital</h2>
                    <p className="text-xl font-bold text-indigo-600">{formatNumberWithCommas(startupCapital)}</p>
                </div>

                {/* ---------- BREAK EVEN ESTIMATION ---------- */}
                <div className="mt-8 p-4 bg-purple-100 rounded-lg shadow-inner border border-purple-300">
                    <h2 className="font-bold text-lg mb-2 text-purple-800">Investment to break Even </h2>
                    <p className="text-xl font-bold text-purple-600">{formatNumberWithCommas(startupCapital * 4.5)}</p>
                </div>

                <div className="mt-6 flex justify-between">
                    <button
                        onClick={() => navigate(`/summary/${cleanKibaloId}`)}
                        className="px-4 py-2 rounded-md shadow text-sm font-medium text-white bg-gray-500 hover:bg-gray-600"
                    >
                        Back to Summary Page
                    </button>
                   
                </div>
            </div>

            {/* ---------- ADD REVENUE MODAL ---------- */}
            {showAddRevenueModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
                    <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
                        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Add/Edit Sale Revenue</h2>
                        <div className="space-y-3">
                            {['name', 'unit', 'quantity', 'costPrice'].map(field => (
                                <div key={field}>
                                    <label className="block text-sm font-medium text-gray-700 capitalize">{field}</label>
                                    <input
                                        type={['quantity', 'costPrice'].includes(field) ? 'number' : 'text'}
                                        value={newRevenue[field]}
                                        onChange={e => setNewRevenue({ ...newRevenue, [field]: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-green-500 focus:border-green-500"
                                        min="0"
                                        step="any"
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => setShowAddRevenueModal(false)}
                                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg shadow text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddRevenue}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow text-sm"
                            >
                                Save Revenue
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnitPage;